import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';

function CampaignApprovals() {
  const { token } = useAuth();
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reject Modal State
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/campaigns/pending`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setPendingCampaigns(await res.json());
        setError(null);
      } else {
        setError('Failed to fetch pending campaigns');
      }
    } catch (e) {
      setError('Network error fetching pending campaigns');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (campaignId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/campaigns/${campaignId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        fetchPending();
        alert('Campaign approved and scheduled for publication!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to approve campaign due to collision or error');
      }
    } catch (e) {
      alert('Network error approving campaign');
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectId || !rejectReason.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/campaigns/${rejectId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ rejection_reason: rejectReason })
      });
      if (res.ok) {
        setRejectId(null);
        setRejectReason('');
        fetchPending();
        alert('Campaign rejected.');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to reject campaign');
      }
    } catch (e) {
      alert('Network error rejecting campaign');
    }
  };

  if (loading) {
    return <div className="card"><p>Loading Pending Campaign Approvals...</p></div>;
  }

  return (
    <div>
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3>Campaign Moderation & Approval Queue</h3>
        <p style={{ color: '#666', marginTop: '4px' }}>Review pending banner artwork, CTA links, target facility associations, and enforce slot collision rules.</p>
      </div>

      {pendingCampaigns.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
          <p style={{ color: 'green', fontWeight: 'bold', fontSize: '16px' }}>
            ✅ All clear! No pending campaign approvals in queue.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {pendingCampaigns.map((c) => (
            <div key={c.id} className="card" style={{ borderLeft: '6px solid #FF9800' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 180px', gap: '20px', alignItems: 'center' }}>
                {/* Banner Preview */}
                <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                  {c.image_url ? (
                    <img src={c.image_url} alt="Banner Preview" style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px' }} />
                  ) : (
                    <div style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#999', fontSize: '12px' }}>
                      No Image Provided
                    </div>
                  )}
                  <span className="badge-info" style={{ marginTop: '6px', display: 'inline-block', fontSize: '10px' }}>
                    {c.placement_slot}
                  </span>
                </div>

                {/* Details */}
                <div>
                  <div style={{ fontSize: '12px', color: '#888' }}>Advertiser: <strong>{c.account_name}</strong></div>
                  <h3 style={{ margin: '4px 0 8px 0' }}>{c.title}</h3>
                  <p style={{ color: '#555', fontSize: '14px', marginBottom: '8px' }}>{c.message || 'No description provided.'}</p>
                  
                  <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '16px' }}>
                    <div>📅 Start: <strong>{new Date(c.campaign_start_at).toLocaleDateString()}</strong></div>
                    <div>📅 End: <strong>{new Date(c.campaign_end_at).toLocaleDateString()}</strong></div>
                    {c.target_facility_name && (
                      <div>🏢 Facility: <strong>{c.target_facility_name}</strong></div>
                    )}
                  </div>

                  {c.cta_label && (
                    <div style={{ marginTop: '8px', fontSize: '12px' }}>
                      🔗 CTA: <span className="badge-success">{c.cta_label}</span> ({c.cta_target_url || 'No URL'})
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button className="btn-primary" style={{ background: '#2E7D32' }} onClick={() => handleApprove(c.id)}>
                    ✓ Approve & Publish
                  </button>
                  <button className="btn-outline" style={{ color: '#C62828', borderColor: '#C62828' }} onClick={() => setRejectId(c.id)}>
                    ✕ Reject Campaign
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '400px', background: '#fff' }}>
            <h3 style={{ marginBottom: '16px' }}>Reject Campaign</h3>
            <form onSubmit={handleReject}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Rejection Reason *</label>
                <textarea placeholder="e.g. Artwork resolution too low / Invalid CTA link" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '80px' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setRejectId(null)}>Cancel</button>
                <button type="submit" className="btn-primary" style={{ background: '#C62828' }}>Confirm Rejection</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignApprovals;
